import "@/App.css";
import "@/styles/metallic-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from "react";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import AssessmentPage from "@/pages/AssessmentPage";
import ConfirmationPage from "@/pages/ConfirmationPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import XrayTemplatePreview from "@/pages/admin/XrayTemplatePreview";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminBulkUpload from "@/pages/admin/AdminBulkUpload";
import AdminCandidateDetail from "@/pages/admin/AdminCandidateDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminXrayCohort from "@/pages/admin/AdminXrayCohort";
import ExecutiveLogin from "@/pages/executive/ExecutiveLogin";
import ExecutiveDashboard from "@/pages/executive/ExecutiveDashboard";
import EmployeeProfile from "@/pages/executive/EmployeeProfile";
import CHROLogin from "@/pages/chro/CHROLogin";
import CHRODashboard from "@/pages/chro/CHRODashboard";
import HomePage from "@/pages/HomePage";

// New RBAC Pages
import UnifiedLogin from "@/pages/UnifiedLogin";
import RegionalVPDashboard from "@/pages/regional/RegionalVPDashboard";
import RegionalHRDashboard from "@/pages/regional/RegionalHRDashboard";
import ManagerDashboard from "@/pages/manager/ManagerDashboard";
import TalentSummary from "@/pages/talent/TalentSummary";
import ProjectManagerDashboard from "@/pages/project/ProjectManagerDashboard";

// Dynamic Assessment Pages
import DynamicAssessmentForm from "@/pages/assessment/DynamicAssessmentForm";
import TALandingPage from "@/pages/assessment/TALandingPage";
import TAConfirmationPage from "@/pages/assessment/TAConfirmationPage";
import TAAdminDashboard from "@/pages/admin/TAAdminDashboard";

// Support Pages
import {
  SupportDashboard,
  IssueReportForm,
  IssueList,
  IssueDetail,
  SystemHealth
} from "@/pages/support";

// Super Support Pages
import { SuperSupportDashboard } from "@/pages/supersupport";

// Demo Executive Dashboard Pages
import DemoCommandCenter from "@/pages/executive/DemoCommandCenter";
import DemoIntelligence from "@/pages/executive/DemoIntelligence";
import DemoPeopleDirectory from "@/pages/executive/DemoPeopleDirectory";
import DemoEmployeeProfile from "@/pages/executive/DemoEmployeeProfile";
import DemoWorkforceHeatmap from "@/pages/executive/DemoWorkforceHeatmap";
import DemoAlerts from "@/pages/executive/DemoAlerts";
import LockedFeature from "@/pages/executive/LockedFeature";
import DemoSettings from "@/pages/executive/DemoSettings";
import TechnicalAssessmentPage from "@/pages/executive/TechnicalAssessmentPage";

// Regional Leader imports
import RegionalLogin from "@/pages/regional/RegionalLogin";
import RegionalOverview from "@/pages/regional/RegionalOverview";
import TeamComposition from "@/pages/regional/TeamComposition";
import SkillsLandscape from "@/pages/regional/SkillsLandscape";
import ManagerValidation from "@/pages/regional/ManagerValidation";
import ICareCollaboration from "@/pages/regional/ICareCollaboration";
import DevelopmentReadiness from "@/pages/regional/DevelopmentReadiness";
import AssessmentInsights from "@/pages/regional/AssessmentInsights";

// Regional VP Redirect Component
function RegionalVPRedirect() {
  const [redirect, setRedirect] = useState(null);
  
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setRedirect("/login");
      return;
    }
    
    // Decode JWT to get region
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const region = payload.region || 'north_america';
      setRedirect(`/regional/${region}`);
    } catch {
      setRedirect("/login");
    }
  }, []);
  
  if (!redirect) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div></div>;
  return <Navigate to={redirect} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/assessment/:token" element={<LandingPage />} />
            <Route path="/assessment/:token/form" element={<AssessmentPage />} />
            <Route path="/assessment/:token/confirmation" element={<ConfirmationPage />} />
            
            {/* Dynamic Assessment Routes (Public) */}
            <Route path="/dynamic-assessment/:accessToken" element={<DynamicAssessmentForm />} />
            
            {/* TA/HOS Assessment Routes */}
            <Route path="/ta-assessment/:accessToken" element={<TALandingPage />} />
            <Route path="/ta-assessment/:accessToken/confirmation" element={<TAConfirmationPage />} />
            
            {/* Unified Login */}
            <Route path="/login" element={<UnifiedLogin />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/xray-template-preview" element={<XrayTemplatePreview />} />
            <Route path="/admin/xray-template-preview/:candidateId" element={<XrayTemplatePreview />} />
            <Route path="/executive/xray-template-preview/:candidateId" element={<XrayTemplatePreview />} />
            <Route path="/admin/xray-cohort" element={<AdminXrayCohort />} />
            <Route path="/admin/upload" element={<AdminUpload />} />
            <Route path="/admin/bulk-upload" element={<AdminBulkUpload />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/candidate/:candidateId" element={<AdminCandidateDetail />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/ta-dashboard" element={<TAAdminDashboard />} />
            
            {/* Executive Routes (Neil) */}
            <Route path="/executive" element={<ExecutiveLogin />} />
            <Route path="/executive/dashboard" element={<ExecutiveDashboard />} />
            <Route path="/executive/profile/:employeeId" element={<EmployeeProfile />} />
            <Route path="/executive/analytics" element={<AdminAnalytics />} />
            <Route path="/executive/xray-cohort" element={<AdminXrayCohort />} />
            
            {/* Demo Executive Dashboard */}
            <Route path="/executive/demo/dashboard" element={<DemoCommandCenter />} />
            <Route path="/executive/demo/intelligence" element={<DemoIntelligence />} />
            <Route path="/executive/demo/people" element={<DemoPeopleDirectory />} />
            <Route path="/executive/demo/people/:id" element={<DemoEmployeeProfile />} />
            <Route path="/executive/demo/heatmap" element={<DemoWorkforceHeatmap />} />
            <Route path="/executive/demo/alerts" element={<DemoAlerts />} />
            <Route path="/executive/demo/technical-assessment" element={<TechnicalAssessmentPage />} />
            <Route path="/executive/demo/talent-strategy" element={<LockedFeature />} />
            <Route path="/executive/demo/workforce-planning" element={<LockedFeature />} />
            <Route path="/executive/demo/roi" element={<LockedFeature />} />
            <Route path="/executive/demo/settings" element={<DemoSettings />} />
            
            {/* CHRO Routes (Paula) */}
            <Route path="/chro" element={<CHROLogin />} />
            <Route path="/chro/dashboard" element={<CHRODashboard />} />
            
            {/* Regional VP Routes */}
            <Route path="/regional-vp" element={<RegionalVPRedirect />} />
            <Route path="/regional/:region" element={<RegionalVPDashboard />} />
            
            {/* Regional HR Routes */}
            <Route path="/regional-hr/:region" element={<RegionalHRDashboard />} />
            
            {/* Manager Routes */}
            <Route path="/manager" element={<ManagerDashboard />} />
            
            {/* Talent Routes */}
            <Route path="/my-assessment" element={<TalentSummary />} />
            
            {/* Project Manager Routes */}
            <Route path="/project-manager" element={<ProjectManagerDashboard />} />
            
            {/* Regional Leader Routes */}
            <Route path="/leader" element={<RegionalLogin />} />
            <Route path="/leader/dashboard" element={<RegionalOverview />} />
            <Route path="/leader/team" element={<TeamComposition />} />
            <Route path="/leader/skills" element={<SkillsLandscape />} />
            <Route path="/leader/validation" element={<ManagerValidation />} />
            <Route path="/leader/icare" element={<ICareCollaboration />} />
            <Route path="/leader/readiness" element={<DevelopmentReadiness />} />
            <Route path="/leader/assessment" element={<AssessmentInsights />} />

            {/* Support Routes (Internal Team) */}
            <Route path="/support" element={<SupportDashboard />} />
            <Route path="/support/issues" element={<IssueList />} />
            <Route path="/support/issues/new" element={<IssueReportForm />} />
            <Route path="/support/issues/:issueId" element={<IssueDetail />} />
            <Route path="/support/health" element={<SystemHealth />} />
            
            {/* Super Support Routes */}
            <Route path="/super-support" element={<SuperSupportDashboard />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
